import React, { useEffect, useRef, useState } from 'react';

interface MainRecommendationProps {
  title: string;
  description: string;
  reasons: string[];
}

export default function MainRecommendation({ title, description, reasons }: MainRecommendationProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="px-5 py-10 md:px-8 md:py-14">
      <div
        className={`mx-auto max-w-[480px] transition-all duration-700 md:max-w-[700px] ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <div className="mb-6 flex items-center gap-2">
          <div className="h-px w-6 bg-softgold/40" />
          <span className="text-[11px] font-medium tracking-wider text-softgold">
            추천 결론
          </span>
        </div>

        <div className="rounded-xl border border-softgold/20 bg-navy px-6 py-8 md:px-8 md:py-10">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-softgold/15">
              <i className="ri-check-line text-[13px] text-softgold" />
            </div>
            <span className="text-[11px] font-medium tracking-wider text-softgold">
              당신에게 가장 현실적인 추천
            </span>
          </div>

          <h2 className="mb-3 font-serif text-[22px] font-semibold tracking-tight text-cream md:text-[26px]">
            {title}
          </h2>

          <p className="mb-6 text-[13px] leading-relaxed text-cream/70 md:text-[14px]">
            {description}
          </p>

          <div className="space-y-2.5">
            {reasons.map((reason, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className="mt-1.5 h-1 w-1 rounded-full bg-softgold" />
                <span className="text-[12px] leading-relaxed text-cream/60 md:text-[13px]">
                  {reason}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}